import {
  MAX_MAX_QUESTIONS,
  MIN_MAX_QUESTIONS,
} from "./agents/returns-agents/util/return-reason-conversation-util";
import {
  type AiVerificationType,
  FlowType,
  InputContentType,
  InputPartType,
  InputValidation,
  ReturnOption,
  Step,
  UncoveredPayer,
} from "./return-flow";
import {
  ReturnOptionMethod,
  StoreCreditType,
} from "./return-flow/return-option";
import { TriggerType } from "./return-flow/trigger";

// ── Constants ────────────────────────────────────────────────────────────────

export const SPECIAL_LOCATION_ID = {
  ORIGINAL: "original",
  SHIPMENT_ORIGIN: "shipment-origin",
  REDO_MANAGED: "redo-managed",
} as const;

export type SpecialLocationId =
  (typeof SPECIAL_LOCATION_ID)[keyof typeof SPECIAL_LOCATION_ID];

// ── Helpers ──────────────────────────────────────────────────────────────────

/** The string node ID used by the React Flow editor. */
export type StepRef = string;

export function isFinalizeFlowType(flowType: FlowType | undefined): boolean {
  return (
    flowType === FlowType.FINALIZE_RETURN ||
    flowType === FlowType.FINALIZE_CLAIM
  );
}

// ── State interfaces ─────────────────────────────────────────────────────────
// Defined here so that both the frontend valid() delegates and the model-level
// validateModelStep() share the same concrete types. UI step files import
// these instead of defining them locally.

export interface TreatmentState {
  name: string;
  percentage: number;
  next: StepRef | undefined;
}

export interface AbTestState {
  name: string;
  treatments: TreatmentState[];
  customTitle?: string;
}

export interface AddReturnTagState {
  tags: { name: string; color: string }[];
  next: StepRef | undefined;
  customTitle?: string;
}

export interface AiReturnReasonsState {
  next: StepRef | undefined;
  nextWrongProduct: StepRef | undefined;
  nextDamaged: StepRef | undefined;
  nextExchange: StepRef | undefined;
  additionalAiInfo?: string;
  maxQuestions?: number;
  merchantGuidance?: string;
  customQuestion?: string;
  customExplanationSubtext?: string;
}

export interface BlockState {
  message: string;
  customTitle?: string;
}

export interface DiscountCodeNameState {
  prefix: string;
  suffix: string;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface ExchangeShippingNameState {
  shippingName: string;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface FinalSaleReturnState {
  next?: StepRef;
  customTitle?: string;
}

export interface FlagState {
  reason: string;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface InformationState {
  message: string;
  header: string;
  continueButtonText: string;
  next?: StepRef;
  customTitle?: string;
}

export interface InputState {
  next: StepRef | undefined;
  prompt: string;
  description: string;
  type: InputPartType | undefined;
  validation: InputValidation;
  exampleImage: URL | undefined;
  attempted?: boolean;
  customTitle?: string;
  minLength?: number;
  maxLength?: number;
  contentType?: InputContentType;
  customRegex?: string;
  customRegexErrorMessage?: string;
  useAdvancedValidation?: boolean;
  aiVerification?: AiVerificationType;
  aiVerificationStrictness?: number;
}

export interface ManualReviewState {
  reason: string;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface ChoiceState {
  name: string;
  next: StepRef | undefined;
  code?: string;
  additionalAiInfo?: string;
}

export interface MultipleChoiceState {
  prompt: string;
  choices: ChoiceState[];
  description: string;
  reason: boolean;
  shuffle?: boolean;
  attempted?: boolean;
  customTitle?: string;
  additionalAiInfo?: string;
}

export interface OverrideAdjustmentState {
  adjustmentFlat: string;
  adjustmentPercent: string;
  /** Per-currency flat amount overrides. Keys are Currency enum values, values are amount strings. */
  currencyOverrides?: Record<string, string>;
  perCurrencyAmountsEnabled?: boolean;
  next: StepRef | undefined;
  customTitle?: string;
  isUseItOrLoseIt?: boolean;
}

export interface OverrideGreenReturnState {
  greenReturnOverride: boolean;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface RejectState {
  message: string;
  customTitle?: string;
}

/**
 * UI-specific representation of a return option. Replaces the model's
 * `method: ReturnOptionMethod` + `adjustment: Adjustment` with string-form
 * fields for editing precision, and adds `type`/`creditType` discriminants.
 */
export type ReturnOptionState = Omit<ReturnOption, "method" | "adjustment"> & {
  type: ReturnOptionMethod["type"] | undefined;
  creditType: StoreCreditType;
  adjustmentFlat: string;
  adjustmentPercent: string;
  adjustmentType?: "flat" | "percent";
  /** Per-currency flat amount overrides. Keys are Currency enum values, values are amount strings. */
  currencyOverrides?: Record<string, string>;
  perCurrencyAmountsEnabled?: boolean;
  isUseItOrLoseIt?: boolean;
  refundPointsRedeemedForItem?: boolean;
  deductEarnedPointsFromRefund?: boolean;
  reimburseForFinalSale?: boolean;
};

export interface ReturnState extends Omit<Step.Return, "type" | "options"> {
  options: ReturnOptionState[];
  exchangeAgentOverrides?: Step.ExchangeAgentOverrides;
}

export interface SendEmailState {
  emailId: string;
  emailName?: string;
  emailType?: string;
  next?: StepRef;
  customTitle?: string;
}

export interface SendSmsState {
  smsId: string;
  smsName?: string;
  smsType?: string;
  next?: StepRef;
  customTitle?: string;
}

export interface SetReturnLocationsState {
  locations: string[];
  restockExternalLocationIds?: Record<string, string | null>;
  customTitle?: string;
}

export interface SubmitClaimOption {
  method: ReturnOptionMethod["type"];
  creditType: StoreCreditType;
  requireSendBack: boolean;
  chargeCustomerForNewOrderShipping?: boolean;
  variantExchangeAddOn?: boolean;
  variantExchangeAddOnVariantId?: string;
  variantExchangeAddOnTitle?: string;
  variantExchangeAddOnAcceptButton?: string;
  variantExchangeAddOnRejectButton?: string;
}

export interface SubmitClaimState {
  option: SubmitClaimOption;
  customTitle?: string;
}

export interface SubmitWarrantyOption {
  method: ReturnOptionMethod["type"];
  creditType: StoreCreditType;
  requireSendBack: boolean;
  labelPayer: UncoveredPayer;
  adjustmentFlat: string | undefined;
  adjustmentPercent: string | undefined;
}

export interface SubmitWarrantyState {
  option: SubmitWarrantyOption;
  customTitle?: string;
}

export interface CombineLabelState {
  customTitle?: string;
}

export interface DoNothingState {}

export interface DynamicBundleAdjustmentState {
  next: StepRef | undefined;
  customTitle?: string;
}

export interface SeparateLabelsState {
  customTitle?: string;
}

export interface SubmitRepairOption {
  labelPayer: UncoveredPayer;
  chargeFee: boolean;
}

export interface SubmitRepairState {
  customTitle?: string;
  option: SubmitRepairOption;
}

export interface SubmitWarrantyRegistrationState {
  customTitle?: string;
}

export interface TriggerState {
  triggerType: TriggerType;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface WaitState {
  numDays: number;
  next: StepRef | undefined;
  customTitle?: string;
}

// ── Validation result type ────────────────────────────────────────────────────

export type StepValidationResult =
  | { valid: true }
  | { valid: false; errors: string[] };

// ── Validation functions ─────────────────────────────────────────────────────

export function isAbTestStepValid(state: AbTestState): StepValidationResult {
  const errors: string[] = [];
  if (!state.treatments.length) {
    errors.push("Must have at least one treatment");
  } else {
    const sum = state.treatments.reduce((acc, t) => acc + t.percentage, 0);
    if (sum !== 100) {
      errors.push(`Treatment percentages must sum to 100 (got ${sum})`);
    }
    for (let i = 0; i < state.treatments.length; i++) {
      const t = state.treatments[i]!;
      if (!t.name) errors.push(`treatments[${i}].name cannot be empty`);
      if (t.next === undefined)
        errors.push(`treatments[${i}].next is required`);
      if (t.percentage < 0 || t.percentage > 100) {
        errors.push(
          `treatments[${i}].percentage must be between 0 and 100 (got ${t.percentage})`,
        );
      }
    }
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isAddReturnTagStepValid(
  state: AddReturnTagState,
  flowType?: FlowType,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.tags.length) {
    errors.push("Must have at least one tag");
  } else {
    for (let i = 0; i < state.tags.length; i++) {
      const tag = state.tags[i]!;
      if (!tag.name) errors.push(`tags[${i}].name cannot be empty`);
      if (!tag.color) errors.push(`tags[${i}].color cannot be empty`);
    }
  }
  if (state.next === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isAiReturnReasonsStepValid(
  state: AiReturnReasonsState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.next) errors.push("next is required");
  if (!state.nextWrongProduct) errors.push("nextWrongProduct is required");
  if (!state.nextDamaged) errors.push("nextDamaged is required");
  if (
    state.maxQuestions !== undefined &&
    (state.maxQuestions < MIN_MAX_QUESTIONS ||
      state.maxQuestions > MAX_MAX_QUESTIONS)
  ) {
    errors.push(
      `maxQuestions must be between ${MIN_MAX_QUESTIONS} and ${MAX_MAX_QUESTIONS} (got ${state.maxQuestions})`,
    );
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isBlockStepValid(state: BlockState): StepValidationResult {
  const errors: string[] = [];
  if (!state.message) errors.push("message cannot be empty");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isConditionStepValid({
  conditionStep,
  flowType,
  isConditionValid,
}: {
  conditionStep: { nextTrue?: StepRef; nextFalse?: StepRef };
  flowType?: FlowType;
  isConditionValid: () => { valid: true } | { valid: false; errors: string[] };
}): StepValidationResult {
  const errors: string[] = [];
  if (conditionStep.nextTrue === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("nextTrue is required");
  }
  if (conditionStep.nextFalse === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("nextFalse is required");
  }
  const conditionValidationResult = isConditionValid();
  if (!conditionValidationResult.valid) {
    errors.push(...conditionValidationResult.errors);
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isDiscountCodeNameStepValid(
  state: DiscountCodeNameState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.prefix && !state.suffix) {
    errors.push("At least one of prefix or suffix must be set");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isExchangeShippingNameStepValid(
  state: ExchangeShippingNameState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.shippingName === undefined) {
    errors.push("shippingName is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isFinalSaleStepValid(
  state: FinalSaleReturnState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined) errors.push("next is required");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isFlagStepValid(
  state: FlagState,
  flowType?: FlowType,
): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isInformationStepValid(
  state: InformationState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.message) errors.push("message cannot be empty");
  if (!state.header) errors.push("header cannot be empty");
  if (!state.continueButtonText) {
    errors.push("continueButtonText cannot be empty");
  }
  if (state.next === undefined) errors.push("next is required");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isInputStepValid(state: InputState): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined) errors.push("next is required");
  if (!state.prompt) errors.push("prompt cannot be empty");
  if (state.type === undefined) errors.push("type is required");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isManualReviewStepValid(
  state: ManualReviewState,
  flowType?: FlowType,
): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isMultipleChoiceStepValid(
  state: MultipleChoiceState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.prompt) errors.push("prompt cannot be empty");
  if (!state.choices.length) {
    errors.push("Must have at least one choice");
  } else {
    for (let i = 0; i < state.choices.length; i++) {
      const choice = state.choices[i]!;
      if (!choice.name) errors.push(`choices[${i}].name cannot be empty`);
      if (choice.next === undefined)
        errors.push(`choices[${i}].next is required`);
    }
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isOverrideAdjustmentStepValid(
  state: OverrideAdjustmentState,
): StepValidationResult {
  const errors: string[] = [];
  if (
    state.adjustmentFlat === undefined &&
    state.adjustmentPercent === undefined
  ) {
    errors.push(
      "At least one of adjustmentFlat or adjustmentPercent must be set",
    );
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isOverrideGreenReturnStepValid(
  state: OverrideGreenReturnState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.greenReturnOverride === undefined) {
    errors.push("greenReturnOverride is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isRejectStepValid(state: RejectState): StepValidationResult {
  const errors: string[] = [];
  if (!state.message) errors.push("message cannot be empty");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isReturnStepValid(state: ReturnState): StepValidationResult {
  const errors: string[] = [];
  if (!state.options.length) {
    errors.push("Must have at least one option");
  } else {
    for (let i = 0; i < state.options.length; i++) {
      const option = state.options[i]!;
      if (isNaN(parseFloat(option.adjustmentFlat))) {
        errors.push(`options[${i}].adjustmentFlat must be a valid number`);
      }
      if (isNaN(parseFloat(option.adjustmentPercent))) {
        errors.push(`options[${i}].adjustmentPercent must be a valid number`);
      }
      if (
        option.type === ReturnOptionMethod.EXCHANGE &&
        option.variantExchangeAddOn
      ) {
        if (!option.variantExchangeAddOnVariantId) {
          errors.push(
            `options[${i}].variantExchangeAddOnVariantId: Required when variantExchangeAddOn is true`,
          );
        }
        if (!option.variantExchangeAddOnTitle) {
          errors.push(
            `options[${i}].variantExchangeAddOnTitle: Required when variantExchangeAddOn is true`,
          );
        }
        if (!option.variantExchangeAddOnAcceptButton) {
          errors.push(
            `options[${i}].variantExchangeAddOnAcceptButton: Required when variantExchangeAddOn is true`,
          );
        }
        if (!option.variantExchangeAddOnRejectButton) {
          errors.push(
            `options[${i}].variantExchangeAddOnRejectButton: Required when variantExchangeAddOn is true`,
          );
        }
      }
    }
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSendEmailStepValid(
  state: SendEmailState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.emailId) errors.push("emailId cannot be empty");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSendSmsStepValid(state: SendSmsState): StepValidationResult {
  const errors: string[] = [];
  if (!state.smsId) errors.push("smsId cannot be empty");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSetReturnLocationsStepValid(
  state: SetReturnLocationsState,
): StepValidationResult {
  const errors: string[] = [];
  const validLocations = state.locations.filter((loc) => loc !== "");
  if (validLocations.length === 0) {
    errors.push("At least one return location must be set");
  } else {
    for (const locationId of validLocations) {
      if (
        locationId === SPECIAL_LOCATION_ID.ORIGINAL ||
        locationId === SPECIAL_LOCATION_ID.REDO_MANAGED
      ) {
        continue;
      }
      const restockId = state.restockExternalLocationIds?.[locationId];
      if (!restockId) {
        errors.push(
          `Location "${locationId}" is missing a restock external location ID`,
        );
      }
    }
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSubmitClaimStepValid(
  state: SubmitClaimState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.option?.method) errors.push("option.method is required");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSubmitWarrantyStepValid(
  state: SubmitWarrantyState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.option?.method) errors.push("option.method is required");
  if (isNaN(parseFloat(state.option.adjustmentFlat ?? "0"))) {
    errors.push("option.adjustmentFlat must be a valid number");
  }
  if (isNaN(parseFloat(state.option.adjustmentPercent ?? "0"))) {
    errors.push("option.adjustmentPercent must be a valid number");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isTriggerStepValid(state: TriggerState): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined) errors.push("next is required");
  if (!state.triggerType) errors.push("triggerType is required");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isWaitStepValid(state: WaitState): StepValidationResult {
  const errors: string[] = [];
  if (!(state.numDays > 0)) errors.push("numDays must be greater than 0");
  if (state.next === undefined) errors.push("next is required");
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isCombineLabelStepValid(
  _state: CombineLabelState,
): StepValidationResult {
  return { valid: true };
}

export function isDynamicBundleAdjustmentStepValid(
  _state: DynamicBundleAdjustmentState,
): StepValidationResult {
  return { valid: true };
}

export function isDoNothingStepValid(
  _state: DoNothingState,
): StepValidationResult {
  return { valid: true };
}

export function isSeparateLabelsStepValid(
  _state: SeparateLabelsState,
): StepValidationResult {
  return { valid: true };
}

export function isSubmitRepairStepValid(
  _state: SubmitRepairState,
): StepValidationResult {
  return { valid: true };
}

export function isSubmitWarrantyRegistrationStepValid(
  _state: SubmitWarrantyRegistrationState,
): StepValidationResult {
  return { valid: true };
}
